/**
 * Utility functions for API URL construction
 */

// Get the base API URL from environment variable or default to localhost
export const getApiBaseUrl = () => {
  return import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api';
};

/**
 * Constructs a full API URL from a relative path
 * @param {string} path - The API path (e.g., '/system-files/summary' or 'system-files/summary')
 * @returns {string} - The full API URL
 */
export const getApiUrl = (path) => {
  const baseUrl = getApiBaseUrl();
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  
  // If path starts with 'api/', remove it since baseUrl includes it
  const finalPath = cleanPath.startsWith('api/') ? cleanPath.slice(4) : cleanPath;
  
  return `${cleanBaseUrl}/${finalPath}`;
};

/**
 * Fetch wrapper that automatically uses the correct API base URL and adds auth headers
 * @param {string} path - The API path
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>}
 */
export const apiFetch = async (path, options = {}) => {
  // Get auth token from localStorage
  const token = localStorage.getItem('auth_token');
  
  // Add Authorization header if token exists
  const headers = {
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Merge headers into options
  const fetchOptions = {
    ...options,
    headers,
  };
  
  const response = await fetch(getApiUrl(path), fetchOptions);
  
  // Handle 401 Unauthorized - redirect to login (but not if already on login page)
  if (response.status === 401 && !window.location.pathname.includes('/login')) {
    console.warn('Unauthorized access - redirecting to login');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    // Use replace instead of href to prevent history entry
    window.location.replace('/login');
    throw new Error('Unauthorized');
  }
  
  // Parse JSON response
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
  }
  
  const jsonData = await response.json();
  return jsonData;
};

